/* global GM_STATE */
import { addMultipleCartItems, getCart, getMaxQuantityByVariant, updateCart, updateCartItem } from 'libs/cart'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { useState, useEffect, useMemo } from 'preact/hooks'
import ButtonATC from 'snippets/button-atc/button-atc'
import ButtonATCDisabled from 'snippets/button-atc/button-atc-disabled'
import { giftWrapSelectors, hideModal, select } from '@/components/helpers/dom'
import useCartGlobal from '@/components/hooks/useCartGlobal'

function ProductATC({
  currentVariant,
  buttonText = translate.product.addToCart,
  customBISFunc,
  buttonClass = '',
  isPreOrder = false,
  isGiftCard,
  hasEngraving = false,
  hasPersonalise = false,
  containerClass = '',
  productProperties,
  propertiesGiftWrapping,
  isCartPage,
  quantityProduct,
  engravingProperties = {}
}) {
  const [isAdding, setIsAdding] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [reRenderKey, setReRenderKey] = useState(1)
  const { maxQuantity, syncMaxQuantity } = useCartGlobal(currentVariant)
  const { productGiftWrapping } = GM_STATE.product
  const hasGiftWrap = currentVariant?.gift_wrap_product

  const resetQuantity = (value = 1) => {
    setQuantity(value)
    setReRenderKey(reRenderKey + 1)
  }

  // Function to get pre-order data from metafields or properties
  const getPreOrderInfo = () => {
    if (GM_STATE.product.metafields?.product_badges === 'Pre-Order') {
      return GM_STATE.product.metafields.pre_order_eta
        ? { 'Pre-Order': GM_STATE.product.metafields.pre_order_eta } : null
    }

    if (currentVariant?.metafields?.product_badges === 'Pre-Order') {
      return currentVariant.metafields.pre_order_eta
        ? { 'Pre-Order': currentVariant.metafields.pre_order_eta } : null
    }

    const existingProperties = currentVariant?.properties || {}
    const preOrderProperty = Object.entries(existingProperties).find(
      ([key, value]) => key.toLowerCase() === 'pre-order' && value !== undefined
    )

    if (preOrderProperty) {
      return {
        'Pre-Order': preOrderProperty[1]
      }
    }

    return null
  }

  const syncDataATC = () => {
    const existingProperties = currentVariant?.properties || {}
    // Use existing referenceID if available, otherwise generate new one
    const existingReferenceID = existingProperties.referenceID || currentVariant?.gift_wrap_product?.properties?.referenceID
    const referenceID = existingReferenceID || new Date().getTime()
    const preOrderInfo = getPreOrderInfo()
    const mergedProperties = { ...existingProperties }

    if (preOrderInfo) {
      Object.entries(preOrderInfo).forEach(([key, value]) => {
        mergedProperties[key] = value
      })
    }

    const itemProduct = {
      id: hasGiftWrap ? currentVariant.key : currentVariant.id,
      quantity: 1,
      properties: hasGiftWrap
        ? { ...mergedProperties, referenceID: currentVariant.properties.referenceID }
        : propertiesGiftWrapping
          ? { ...mergedProperties, referenceID }
          : { ...mergedProperties }
    }

    const itemGift = {}

    if (propertiesGiftWrapping) {
      const { options, messages } = propertiesGiftWrapping
      const quantityATC = messages.length

      itemProduct.quantity = quantityProduct

      for (const [key, value] of Object.entries(options)) {
        itemProduct.properties[key] = value.name
      }

      messages.forEach((property, index) => {
        const { message, isGiftReceipt } = property

        itemProduct.properties[`card_message_${index + 1}`] = message
        itemProduct.properties[`card_gift_receipt_${index + 1}`] = isGiftReceipt
      })

      itemGift.id = hasGiftWrap ? currentVariant.gift_wrap_product.key : productGiftWrapping.initVariant.id
      itemGift.quantity = quantityATC

      const giftProperties = {
        referenceID: hasGiftWrap ? currentVariant.properties.referenceID : referenceID
      }

      if (preOrderInfo) {
        Object.entries(preOrderInfo).forEach(([key, value]) => {
          giftProperties[key] = value
        })
      }

      itemGift.properties = hasGiftWrap
        ? { ...currentVariant.gift_wrap_product.properties, ...giftProperties }
        : giftProperties
    }

    return { itemGift, itemProduct, referenceID: hasGiftWrap ? currentVariant.properties.referenceID : propertiesGiftWrapping ? referenceID : null }
  }

  const addToCart = async () => {
    if (isAdding) return // Prevent double-clicks

    try {
      setIsAdding(true)

      const products = {
        items: []
      }
      const { itemGift, itemProduct, referenceID } = syncDataATC()

      if (propertiesGiftWrapping) {
        products.items.push(itemGift)
      }

      // itemProduct.properties = { ...itemProduct.properties, ...productProperties }

      if (currentVariant?.engraving_product) {
        itemProduct.properties = { ...itemProduct.properties, ...engravingProperties }
      }

      if (currentVariant?.key) {
        // if product added to cart, just update quantity product, gift will ATC by func addMultipleCartItems
        await updateCartItem(currentVariant?.key, itemProduct.quantity, itemProduct.properties)
        currentVariant?.engraving_product && await updateCartItem(currentVariant.engraving_product.key, itemProduct.quantity, { ...engravingProperties, _isProductEngraving: true, _parentProductSku: currentVariant.sku })
      } else {
        // if ATC product append product data to list and ATC both of product and gift by addMultipleCartItems
        products.items.push(itemProduct)
      }

      const cartSingle = await addMultipleCartItems(products)
      const newCart = await updateCart()

      if (!cartSingle) throw new Error('Can not add to cart')

      hideModal(giftWrapSelectors.MODAL_GIFT_WRAPPING_SELECTOR)
      globalEmitter.emit(emitterProps.cart.add, newCart)

      const max = getMaxQuantityByVariant(cartSingle.items, currentVariant)

      if (max > 0 && max < quantity) {
        resetQuantity(max)
      }

      if (max <= 0) {
        resetQuantity(1)
      }

      await syncMaxQuantity()
    } finally {
      setIsAdding(false)
    }
  }

  const updateToCart = async () => {
    if (isAdding) return // Prevent double-clicks

    try {
      setIsAdding(true)

      const { itemGift, itemProduct, referenceID } = syncDataATC()

      if (engravingProperties?._timestamp) {
        itemProduct.properties = { ...itemProduct.properties, ...engravingProperties }
      }

      const cartSingleProduct = await updateCartItem(itemProduct.id, itemProduct.quantity, { ...itemProduct.properties, ...productProperties })
      const cartSingleGift = await updateCartItem(itemGift.id, itemGift.quantity, itemGift.properties)

      currentVariant?.engraving_product && await updateCartItem(currentVariant.engraving_product.key, itemProduct.quantity, { ...engravingProperties, _isProductEngraving: true, _parentProductSku: currentVariant.sku })

      if (cartSingleGift && cartSingleProduct) {
        hideModal(giftWrapSelectors.MODAL_GIFT_WRAPPING_SELECTOR)
        globalEmitter.emit(emitterProps.cart.update, cartSingleGift.items)
      }

      const max = getMaxQuantityByVariant(cartSingleGift.items, currentVariant)

      if (max > 0 && max < quantity) {
        resetQuantity(max)
      }

      if (max <= 0) {
        resetQuantity(1)
      }

      await syncMaxQuantity()
    } finally {
      setIsAdding(false)
    }
  }

  const handleAddToCart = () => {
    if (hasGiftWrap) {
      updateToCart()
    } else {
      addToCart()
    }
  }

  const handleBIS = () => {
    if (customBISFunc) {
      customBISFunc()

      return
    }

    const el = select('#ProductNotifyModal', document.body)

    if (el) {
      el.show()
    }
  }

  useEffect(() => {
    if (currentVariant) {
      resetQuantity(1)
    }
  }, [currentVariant])

  let isATC = false
  let btnText = buttonText

  if (hasGiftWrap) {
    isATC = true
    btnText = 'UPDATE'
  } else {
    isATC = isCartPage || (currentVariant?.available && maxQuantity > 0)
  }

  return (
    <div className={`w-full ${hasPersonalise ? 'mt-3' : hasEngraving ? (isGiftCard ? 'mt-6' : 'mt-4') : 'mt-8 md:mt-6 lg:mt-10'
      } ${containerClass}`}>
      <div class="js-product-atc">
        {isATC || isGiftCard || isPreOrder ? (
          <div className="flex flex-col gap-4">
            <ButtonATC
              disabled={isAdding}
              text={btnText}
              onHandle={handleAddToCart}
              loading={isAdding}
              buttonClass={buttonClass}
            />
            {isPreOrder && <ButtonATCDisabled text={translate.product.notifyMe} outlined onHandle={handleBIS} />}
          </div>
        ) : (
          <ButtonATCDisabled text={`${translate.product.soldOut} - ${translate.product.notifyMe}`} onHandle={handleBIS} />
        )}
      </div>
    </div>
  )
}

export default ProductATC
